import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Mail, Calendar, ArrowRight, CheckCircle, User, Lock } from 'lucide-react';

const CTASection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit form');
      }
      
      setIsSubmitted(true);
      toast.success(data.message || "Thank you! We'll be in touch within 24 hours.");
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({ name: '', email: '', message: '' });
        setIsSubmitted(false);
      }, 5000);
      
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again or email us directly at info@nursebloc.io');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-gradient-to-b from-violet-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Content side */}
          <div>
            <div className="text-center mb-16">
              <span className="text-fuchsia-500 font-semibold text-sm uppercase tracking-wide">
                Get Started
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mt-2 mb-4">
                Support Built Around the <span className="text-nursebloc-gradient">Care Pair</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-4">
                Care coordination centered on the caregiver and their loved one — not coverage rules.
              </p>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Schedule a free consultation with one of our nurses. No pressure, no commitment 
                — just a conversation about how we might be able to help your family.
              </p>
            </div>

            <div className="space-y-6 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                  <Mail className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Email a real nurse</p>
                  <p className="text-gray-600 text-sm">Not a call center, not a chatbot</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-fuchsia-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-fuchsia-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Free 30-minute consultation</p>
                  <p className="text-gray-600 text-sm">We'll discuss your situation and options</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Quick Response</p>
                  <p className="text-gray-600 text-sm">We'll get back to you soon</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form side */}
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-violet-100">
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-violet-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Thank You!</h3>
                <p className="text-gray-600">We'll be in touch soon to support you on your journey.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <h3 className="text-xl font-bold text-slate-800 mb-2">Get Started with NurseBloc</h3>
                <p className="text-gray-600 text-sm mb-6">Tell us how to reach you and we'll be in touch</p>
                
                <div className="space-y-4">
                  <div>
                    <label 
                      htmlFor="name" 
                      className="block text-sm font-medium text-slate-700 mb-1.5"
                    >
                      Your name <span className="text-red-500" aria-label="required">*</span>
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Your full name"
                      required
                      aria-describedby="name-help"
                      aria-required="true"
                      className="rounded-xl border-violet-200 focus:border-violet-500 focus:ring-violet-500"
                    />
                    <span id="name-help" className="sr-only">
                      Enter your name so we know who we're contacting
                    </span>
                  </div>

                  <div>
                    <label 
                      htmlFor="email" 
                      className="block text-sm font-medium text-slate-700 mb-1.5"
                    >
                      Email address <span className="text-red-500" aria-label="required">*</span>
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                      aria-describedby="email-help"
                      aria-required="true"
                      className="rounded-xl border-violet-200 focus:border-violet-500 focus:ring-violet-500"
                    />
                    <span id="email-help" className="sr-only">
                      We'll use this to respond to your inquiry
                    </span>
                  </div>

                  <div>
                    <label 
                      htmlFor="message" 
                      className="block text-sm font-medium text-slate-700 mb-1.5"
                    >
                      What type of support are you looking for? (optional)
                    </label>
                    <select
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="rounded-xl border-violet-200 focus:border-violet-500 focus:ring-violet-500 w-full px-4 py-2"
                    >
                      <option value="">Select an option</option>
                      <option value="medication-management">Medication Management</option>
                      <option value="care-coordination">Care Coordination</option>
                      <option value="healthcare-navigation">Healthcare Navigation</option>
                      <option value="general-support">General Support</option>
                      <option value="other">Other</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">We'll discuss details during our consultation</p>
                  </div>
                </div>
                
                <div className="flex flex-col gap-4">
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-nursebloc py-6 rounded-full text-lg font-semibold shadow-lg group relative"
                    aria-label="Submit form to contact NurseBloc"
                  >
                    <span className="flex items-center justify-center">
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin mr-2" aria-hidden="true"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          Contact Your Care Partner
                          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                        </>
                      )}
                    </span>
                  </Button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    By submitting, you agree to our privacy policy. We'll never share your information.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
